/**
 * VaultDetailPane - Right pane for item details
 * USER-CON-001 Phase 2
 */
import React, { useState, useEffect } from 'react';
import {
  X, Package, Award, Edit2, Trash2, DollarSign, History,
  MapPin, Tag, Calendar, FileText, ChevronDown, ChevronRight,
  Camera, QrCode, Box, Plus, ExternalLink, Clock, AlertCircle
} from 'lucide-react';
import {
  getConditionEvents,
  getProvenanceSummary,
  createConditionEvent,
  updateVaultItem,
  removeVaultItem,
  addToSellQueue,
  removeFromSellQueue,
} from '../../services/vaultApi';
import SignatureList from './SignatureList';
import VaultUploadWithGrading from './VaultUploadWithGrading';
import GradingResultsCard from './GradingResultsCard';
import CGCLoopCTA from './CGCLoopCTA';
import AutoGradeHistoryPanel from './AutoGradeHistoryPanel';
import { GRADE_OPTIONS } from '../../utils/vaultGrade';
import { pickVaultItemUpdate } from '../../utils/vaultContract';

// Format currency
const formatCurrency = (value) => {
  if (!value) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Event type labels and colors
const EVENT_TYPES = {
  purchase: { label: 'Purchase', color: 'text-green-400', bg: 'bg-green-500/20' },
  sale: { label: 'Sale', color: 'text-red-400', bg: 'bg-red-500/20' },
  grading_submission: { label: 'Grading Submission', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  grading_return: { label: 'Grading Return', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  grade_change: { label: 'Grade Change', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  pressing: { label: 'Pressing', color: 'text-purple-400', bg: 'bg-purple-500/20' },
  restoration: { label: 'Restoration', color: 'text-orange-400', bg: 'bg-orange-500/20' },
  note: { label: 'Note', color: 'text-zinc-400', bg: 'bg-zinc-500/20' },
};

function ProvenanceSection({ item }) {
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (item?.id) {
      loadProvenance();
    }
  }, [item?.id]);

  const loadProvenance = async () => {
    setIsLoading(true);
    try {
      const [eventsResult, summaryResult] = await Promise.all([
        getConditionEvents(item.id),
        getProvenanceSummary(item.id),
      ]);
      setEvents(eventsResult.events || []);
      setSummary(summaryResult);
    } catch (error) {
      console.error('Failed to load provenance:', error);
    }
    setIsLoading(false);
  };

  return (
    <div className="border-t border-zinc-800 pt-4 mt-4">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left mb-3"
      >
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-zinc-400" />
          <span className="font-medium text-white">Provenance & History</span>
        </div>
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-zinc-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-zinc-400" />
        )}
      </button>

      {isExpanded && (
        <div className="space-y-3">
          {/* Summary stats */}
          {summary && summary.total_events > 0 && (
            <div className="grid grid-cols-2 gap-2 text-xs">
              {summary.total_investment && (
                <div className="bg-zinc-800/50 rounded p-2">
                  <span className="text-zinc-500">Total Investment</span>
                  <p className="text-green-400 font-medium">
                    {formatCurrency(summary.total_investment)}
                  </p>
                </div>
              )}
              {summary.grade_changes > 0 && (
                <div className="bg-zinc-800/50 rounded p-2">
                  <span className="text-zinc-500">Grade Changes</span>
                  <p className="text-yellow-400 font-medium">{summary.grade_changes}</p>
                </div>
              )}
            </div>
          )}

          {/* Event timeline */}
          {isLoading ? (
            <div className="text-center py-4">
              <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : events.length === 0 ? (
            <p className="text-sm text-zinc-500 text-center py-4">
              No history events recorded yet.
            </p>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {events.map((event) => {
                const eventType = EVENT_TYPES[event.event_type] || EVENT_TYPES.note;
                return (
                  <div
                    key={event.id}
                    className="flex items-start gap-2 text-sm"
                  >
                    <div className={`w-2 h-2 rounded-full ${eventType.bg} mt-1.5`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className={eventType.color}>{eventType.label}</span>
                        <span className="text-xs text-zinc-500">
                          {formatDate(event.event_date)}
                        </span>
                      </div>
                      {event.description && (
                        <p className="text-zinc-400 text-xs truncate">
                          {event.description}
                        </p>
                      )}
                      {event.price && (
                        <p className="text-green-400 text-xs">
                          {formatCurrency(event.price)}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function VaultDetailPane({ item, onClose, onUpdate, onDelete }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (item) {
      setEditData({
        user_grade: item.user_grade || '',
        purchase_price: item.purchase_price || '',
        notes: item.notes || '',
      });
      setSaveError(null);
    }
  }, [item]);

  if (!item) {
    return (
      <div className="w-80 bg-zinc-900 border-l border-zinc-800 flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <Package className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">Select an item to view details</p>
          </div>
        </div>
      </div>
    );
  }

  const handleSave = async () => {
    setIsLoading(true);
    setSaveError(null);
    try {
      // purchase_price is Optional[Decimal] server-side, so the empty string this
      // number input produces when cleared is a 422. Coerce THAT FIELD ONLY.
      // notes is Optional[str] and validates happily at '' -- blanket-stripping empty
      // strings would silently drop it from the payload and make it impossible to
      // CLEAR a field once set, which is the same silent-write class this is fixing.
      // user_grade '' is the select's "No Grade" and is sent as null so a grade can
      // be cleared too.
      //
      // V-SCHEMA-FE: CollectionItemUpdate is extra='forbid', so a key it does not
      // declare 422s the WHOLE save. pickVaultItemUpdate keeps only declared keys;
      // the 422 detail (already flattened to "field: message" by fetchAPI) is shown
      // in the pane instead of vanishing into console.error.
      const { payload } = pickVaultItemUpdate({
        ...editData,
        user_grade: editData.user_grade === '' ? null : editData.user_grade,
        purchase_price: editData.purchase_price === '' ? null : editData.purchase_price,
      });
      await updateVaultItem(item.id, payload);
      onUpdate?.();
      setIsEditing(false);
    } catch (error) {
      setSaveError(error.message || 'Failed to update item');
    }
    setIsLoading(false);
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to remove this item from your vault?')) return;

    setIsLoading(true);
    try {
      await removeVaultItem(item.id);
      onDelete?.(item.id);
      onClose?.();
    } catch (error) {
      console.error('Failed to delete item:', error);
    }
    setIsLoading(false);
  };

  const handleAddToSellQueue = async () => {
    setIsLoading(true);
    try {
      await addToSellQueue([item.id]);
      onUpdate?.();
    } catch (error) {
      console.error('Failed to add to sell queue:', error);
    }
    setIsLoading(false);
  };

  const handleRemoveFromSellQueue = async () => {
    setIsLoading(true);
    try {
      await removeFromSellQueue([item.id]);
      onUpdate?.();
    } catch (error) {
      console.error('Failed to remove from sell queue:', error);
    }
    setIsLoading(false);
  };

  return (
    <div className="w-80 bg-zinc-900 border-l border-zinc-800 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
        <h3 className="font-medium text-white truncate flex-1">Item Details</h3>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="p-1.5 hover:bg-zinc-800 rounded transition-colors"
            title="Edit"
          >
            <Edit2 className="w-4 h-4 text-zinc-400" />
          </button>
          <button
            onClick={handleDelete}
            className="p-1.5 hover:bg-zinc-800 rounded transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4 text-red-400" />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-zinc-800 rounded transition-colors"
            title="Close"
          >
            <X className="w-4 h-4 text-zinc-400" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Cover Image */}
        <div className="aspect-[2/3] bg-zinc-800 rounded-lg overflow-hidden mb-4 relative group">
          {item.cover_photo_url ? (
            <img
              src={item.cover_photo_url}
              alt={item.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className="w-16 h-16 text-zinc-700" />
            </div>
          )}

          {/* Badges */}
          <div className="absolute top-2 right-2 flex flex-col gap-1">
            {item.is_graded && (
              <span className="px-2 py-0.5 bg-green-500/90 text-white text-xs font-bold rounded uppercase">
                {item.grading_company?.toUpperCase()}
              </span>
            )}
            {item.is_signed && (
              <span className="px-2 py-0.5 bg-purple-500/90 text-white text-xs font-bold rounded">
                Signed
              </span>
            )}
            {item.is_for_sale && (
              <span className="px-2 py-0.5 bg-orange-500/90 text-white text-xs font-bold rounded">
                For Sale
              </span>
            )}
          </div>
        </div>

        {/* Title & Issue */}
        <h4 className="text-lg font-bold text-white mb-1">{item.title || 'Untitled'}</h4>
        <p className="text-sm text-zinc-400 mb-4">
          #{item.issue_number || '?'}
          {item.year && ` (${item.year})`}
          {item.publisher && ` - ${item.publisher}`}
        </p>

        {/* Key Details */}
        <div className="space-y-3 text-sm">
          {/* Grade */}
          <div className="flex items-center justify-between">
            <span className="text-zinc-500 flex items-center gap-2">
              <Award className="w-4 h-4" />
              Grade
            </span>
            {isEditing ? (
              <select
                value={editData.user_grade}
                onChange={(e) => setEditData({ ...editData, user_grade: e.target.value })}
                className="w-28 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-white text-right text-sm"
                aria-label="Grade"
                data-testid="detail-grade-select"
              >
                <option value="">No Grade</option>
                {/* A legacy free-text grade stays selectable rather than silently changing */}
                {editData.user_grade && !GRADE_OPTIONS.includes(editData.user_grade) && (
                  <option value={editData.user_grade}>{editData.user_grade}</option>
                )}
                {GRADE_OPTIONS.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            ) : (
              <span className={`font-medium ${item.is_graded ? 'text-green-400' : 'text-yellow-400'}`}>
                {item.is_graded
                  ? `${item.grading_company?.toUpperCase()} ${item.user_grade}`
                  : item.user_grade || 'Ungraded'}
              </span>
            )}
          </div>

          {/* Certificate Number */}
          {item.cert_number && (
            <div className="flex items-center justify-between">
              <span className="text-zinc-500 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Cert #
              </span>
              <span className="text-white font-mono">{item.cert_number}</span>
            </div>
          )}

          {/* Purchase Price */}
          <div className="flex items-center justify-between">
            <span className="text-zinc-500 flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Purchase Price
            </span>
            {isEditing ? (
              <input
                type="number"
                value={editData.purchase_price}
                onChange={(e) => setEditData({ ...editData, purchase_price: e.target.value })}
                className="w-24 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-white text-right text-sm"
                placeholder="0.00"
                step="0.01"
              />
            ) : (
              <span className="text-green-400">{formatCurrency(item.purchase_price)}</span>
            )}
          </div>

          {/* Market Value */}
          {item.market_value && (
            <div className="flex items-center justify-between">
              <span className="text-zinc-500 flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Market Value
              </span>
              <span className="text-blue-400">{formatCurrency(item.market_value)}</span>
            </div>
          )}

          {/* Box Location */}
          {item.box_id && (
            <div className="flex items-center justify-between">
              <span className="text-zinc-500 flex items-center gap-2">
                <Box className="w-4 h-4" />
                Box Location
              </span>
              <span className="text-white">Box #{item.box_id}</span>
            </div>
          )}

          {/* Date Added */}
          <div className="flex items-center justify-between">
            <span className="text-zinc-500 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Added
            </span>
            <span className="text-zinc-300">{formatDate(item.created_at)}</span>
          </div>
        </div>

        {/* Edit Actions */}
        {isEditing && (
          <>
            {saveError && (
              <p
                className="mt-3 text-xs text-red-400 bg-red-500/10 border border-red-500/30 rounded px-2 py-1.5"
                role="alert"
                data-testid="detail-save-error"
              >
                {saveError}
              </p>
            )}
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => setIsEditing(false)}
                className="flex-1 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="flex-1 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-zinc-700 text-white rounded-lg transition-colors text-sm"
              >
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </>
        )}

        {/* Sell Queue Toggle */}
        {!isEditing && (
          <div className="mt-4">
            {item.is_for_sale ? (
              <button
                onClick={handleRemoveFromSellQueue}
                disabled={isLoading}
                className="w-full py-2 bg-zinc-800 hover:bg-zinc-700 text-orange-400 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
              >
                <DollarSign className="w-4 h-4" />
                Remove from Sell Queue
              </button>
            ) : (
              <button
                onClick={handleAddToSellQueue}
                disabled={isLoading}
                className="w-full py-2 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
              >
                <DollarSign className="w-4 h-4" />
                Add to Sell Queue
              </button>
            )}
          </div>
        )}

        {/* Auto-Grade Section (C6.P3) */}
        <div className="border-t border-zinc-800 pt-4 mt-4">
          <VaultUploadWithGrading
            vaultItemId={item.id}
            currentPhotoUrl={item.cover_photo_url}
            onGradeComplete={onUpdate}
            onUploadComplete={onUpdate}
          />
          {/* V-1 (D59): THREE names were wrong in this one block and each failed silently.
              * item.auto_grade_result   -> the API serves `ai_grade_result` (the column name).
                `auto_grade_result` has never existed, so this gate was always falsy and the
                results card never rendered at all.
              * ?.grade_value            -> the stored blob is dataclasses.asdict(GradingResult)
                (vault_auto_grade_service.py:181), whose field is `grade`. `grade_value` is a
                column on VaultAutoGradeEvent, a DIFFERENT object -- so even once the outer name
                is right, this read returns undefined.
              * ?.confidence             -> correct; GradingResult does have `confidence`.
              Optional chaining meant every one of these degraded to undefined instead of
              throwing, which is exactly why nothing ever surfaced the mismatch. */}
          {item.ai_grade_result && (
            <GradingResultsCard
              gradeResult={item.ai_grade_result}
              gradeValue={item.ai_grade_result?.grade}
              confidence={item.ai_grade_result?.confidence}
              showCGCCta={true}
              vaultItemId={item.id}
            />
          )}
          {item.auto_grade_status === 'completed' && (
            <CGCLoopCTA
              vaultItemId={item.id}
              gradeValue={item.auto_grade_value ?? item.ai_grade_result?.grade}
              confidence={item.ai_grade_result?.confidence}
            />
          )}
          {/* V-3 (D60): the `history={item.auto_grade_history || []}` prop is GONE. That field
              was never served, so it resolved to [] on every render and the panel asserted
              "No grading history" over real events -- a FALSE STATEMENT, not an empty state.
              The panel now fetches per-item history itself from the vaultItemId it already had. */}
          <AutoGradeHistoryPanel vaultItemId={item.id} />
        </div>

        {/* Signature Tracking Section (Phase 7) */}
        <SignatureList
          collectionItemId={item.id}
          onSignaturesChange={onUpdate}
        />

        {/* Provenance Section */}
        <ProvenanceSection item={item} />
      </div>
    </div>
  );
}
